import React, { useState, useEffect } from "react";
import DocumentUpload from "../../components/DocumentUpload";
import LocationSelectors from "../../components/LocationSelectors";
import { useNavigate, useLocation } from "react-router-dom";
import "../../styles/FormPages.css";

const PropertyFormPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { country, state: stateVal, district, lawCategory } = location.state || {};

  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;

  const [formData, setFormData] = useState({
    country: country || "",
    state: stateVal || "",
    district: district || "",

    applicantFullName: "",
    applicantFatherOrHusband: "",
    applicantAge: "",
    applicantGender: "",
    applicantOccupation: "",
    applicantContact: "",
    applicantAddress: "",
    documentType: "",
    documentTypeOther: "",
    propertyType: "",
    propertyTypeOther: "",
    propertyLocation: "",
    propertyDescription: "",
    propertyDocuments: "",
    transactionType: "",
    transactionAmount: "",
    transactionDetails: "",
    partiesInvolved: "",
    legalRequirements: "",
    dueDate: "",
    declaration: false,
  });

  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [extractedContent, setExtractedContent] = useState({});

  const handleDocumentsChange = (files, content) => {
    setUploadedDocuments(files);
    setExtractedContent(content);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const selectedCountry = formData.country || country || "";
    const selectedState = formData.state || stateVal || "";
    const selectedDistrict = formData.district || district || "";
    
    let detailsText = `
👤 Applicant Details:
Full Name: ${formData.applicantFullName}
Father's/Husband's Name: ${formData.applicantFatherOrHusband}
Age: ${formData.applicantAge}
Gender: ${formData.applicantGender}
Occupation: ${formData.applicantOccupation}
Contact: ${formData.applicantContact}
Address: ${formData.applicantAddress}

🏠 Property Details:
Document Type: ${formData.documentTypeOther || formData.documentType}
Property Type: ${formData.propertyTypeOther || formData.propertyType}
Property Location: ${formData.propertyLocation}
Property Description: ${formData.propertyDescription}
Property Documents: ${formData.propertyDocuments}

💰 Transaction Details:
Transaction Type: ${formData.transactionType}
Transaction Amount: ${formData.transactionAmount}
Transaction Details: ${formData.transactionDetails}
Parties Involved: ${formData.partiesInvolved}

⚖️ Legal Requirements:
Legal Requirements: ${formData.legalRequirements}
Due Date: ${formData.dueDate}
`.trim();

    if (uploadedDocuments.length > 0) {
      detailsText += `\n\n📄 Supporting Documents:`;
      uploadedDocuments.forEach((doc, idx) => {
        detailsText += `\n\nDocument ${idx + 1}: ${doc.name}`;
        if (extractedContent[doc.id]) {
          detailsText += `\nExtracted Content:\n${extractedContent[doc.id]}`;
        }
      });
    }

    navigate("/drafts", {
      state: {
        query: detailsText,
        summary: detailsText,
        country: selectedCountry,
        state: selectedState,
        district: selectedDistrict,
        lawCategory,
  draftType: "property",
  entityName: formData.applicantFullName,
  uploadedDocuments,
  extractedContent,
      },
    });
  };

  const renderStep1 = () => (
    <div className="form-page-section">
      <div className="form-page-section-title">
        👤 Applicant Details
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Full Name *</label>
          <input
            type="text"
            name="applicantFullName"
            value={formData.applicantFullName}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-page-col">
          <label>Father's/Husband's Name *</label>
          <input
            type="text"
            name="applicantFatherOrHusband"
            value={formData.applicantFatherOrHusband}
            onChange={handleInputChange}
            required
          />
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Age *</label>
          <input
            type="number"
            name="applicantAge"
            value={formData.applicantAge}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-page-col">
          <label>Gender *</label>
          <select
            name="applicantGender"
            value={formData.applicantGender}
            onChange={handleInputChange}
            required
          >
            <option value="">Select Gender</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
          </select>
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Occupation *</label>
          <input
            type="text"
            name="applicantOccupation"
            value={formData.applicantOccupation}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-page-col">
          <label>Contact Number *</label>
          <input
            type="tel"
            name="applicantContact"
            value={formData.applicantContact}
            onChange={handleInputChange}
            required
          />
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col full-width">
          <label>Address *</label>
          <textarea
            name="applicantAddress"
            value={formData.applicantAddress}
            onChange={handleInputChange}
            rows={3}
            required
          />
        </div>
      </div>

      <LocationSelectors
        country={formData.country}
        state={formData.state}
        district={formData.district}
        onChange={(next) => setFormData((prev) => ({ ...prev, ...next }))}
      />
    </div>
  );

  const renderStep2 = () => (
    <div className="form-page-section">
      <div className="form-page-section-title">
        📋 Document Details
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Document Type *</label>
          <select
            name="documentType"
            value={formData.documentType}
            onChange={handleInputChange}
            required
          >
            <option value="">Select Document Type</option>
            <option value="Sale Deed">Sale Deed</option>
            <option value="Purchase Agreement">Purchase Agreement</option>
            <option value="Rent Agreement">Rent Agreement</option>
            <option value="Lease Deed">Lease Deed</option>
            <option value="Gift Deed">Gift Deed</option>
            <option value="Power of Attorney">Power of Attorney</option>
            <option value="Property Registration">Property Registration</option>
            <option value="Partition Deed">Partition Deed</option>
            <option value="Other">Other (specify)</option>
          </select>
        </div>
        <div className="form-page-col">
          {formData.documentType === "Other" && (
            <>
              <label>Specify Document Type *</label>
              <input
                type="text"
                name="documentTypeOther"
                value={formData.documentTypeOther}
                onChange={handleInputChange}
                required
              />
            </>
          )}
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Property Type *</label>
          <select
            name="propertyType"
            value={formData.propertyType}
            onChange={handleInputChange}
            required
          >
            <option value="">Select Property Type</option>
            <option value="Residential House">Residential House</option>
            <option value="Apartment/Flat">Apartment/Flat</option>
            <option value="Commercial Building">Commercial Building</option>
            <option value="Plot/Land">Plot/Land</option>
            <option value="Agricultural Land">Agricultural Land</option>
            <option value="Industrial Property">Industrial Property</option>
            <option value="Office Space">Office Space</option>
            <option value="Other">Other (specify)</option>
          </select>
        </div>
        <div className="form-page-col">
          {formData.propertyType === "Other" && (
            <>
              <label>Specify Property Type *</label>
              <input
                type="text"
                name="propertyTypeOther"
                value={formData.propertyTypeOther}
                onChange={handleInputChange}
                required
              />
            </>
          )}
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col full-width">
          <label>Property Location *</label>
          <textarea
            name="propertyLocation"
            value={formData.propertyLocation}
            onChange={handleInputChange}
            rows={3}
            placeholder="Complete address of the property including survey numbers, ward details..."
            required
          />
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="form-page-section">
      <div className="form-page-section-title">
        💰 Property & Transaction Details
      </div>
      <div className="form-page-row">
        <div className="form-page-col full-width">
          <label>Property Description *</label>
          <textarea
            name="propertyDescription"
            value={formData.propertyDescription}
            onChange={handleInputChange}
            rows={4}
            placeholder="Detailed description of the property including size, boundaries, amenities..."
            required
          />
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Transaction Type *</label>
          <select
            name="transactionType"
            value={formData.transactionType}
            onChange={handleInputChange}
            required
          >
            <option value="">Select Transaction Type</option>
            <option value="Sale">Sale</option>
            <option value="Purchase">Purchase</option>
            <option value="Rental">Rental</option>
            <option value="Lease">Lease</option>
            <option value="Gift">Gift</option>
            <option value="Inheritance">Inheritance</option>
            <option value="Mortgage">Mortgage</option>
            <option value="Exchange">Exchange</option>
          </select>
        </div>
        <div className="form-page-col">
          <label>Transaction Amount (₹)</label>
          <input
            type="number"
            name="transactionAmount"
            value={formData.transactionAmount}
            onChange={handleInputChange}
            placeholder="Amount in INR"
          />
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col full-width">
          <label>Transaction Details *</label>
          <textarea
            name="transactionDetails"
            value={formData.transactionDetails}
            onChange={handleInputChange}
            rows={3}
            placeholder="Describe the transaction terms, conditions, and other important details..."
            required
          />
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <>
      <div className="form-page-section">
        <div className="form-page-section-title">
          👥 Additional Information
        </div>
        <div className="form-page-row">
          <div className="form-page-col full-width">
            <label>Property Documents Available</label>
            <textarea
              name="propertyDocuments"
              value={formData.propertyDocuments}
              onChange={handleInputChange}
              rows={3}
              placeholder="List all available documents like sale deed, title documents, survey records..."
            />
          </div>
        </div>
        <div className="form-page-row">
          <div className="form-page-col full-width">
            <label>Parties Involved *</label>
            <textarea
              name="partiesInvolved"
              value={formData.partiesInvolved}
              onChange={handleInputChange}
              rows={3}
              placeholder="Names and details of all parties involved in this transaction..."
              required
            />
          </div>
        </div>
        <div className="form-page-row">
          <div className="form-page-col">
            <label>Legal Requirements/Compliance *</label>
            <textarea
              name="legalRequirements"
              value={formData.legalRequirements}
              onChange={handleInputChange}
              rows={3}
              placeholder="Specific legal requirements, compliance needs, or regulations to follow..."
              required
            />
          </div>
          <div className="form-page-col">
            <label>Due Date</label>
            <input
              type="date"
              name="dueDate"
              value={formData.dueDate}
              onChange={handleInputChange}
            />
          </div>
        </div>
      </div>
      <div className="form-page-declaration">
        <div className="form-page-section-title">
          ✅ Declaration
        </div>
        <label>
          <input
            type="checkbox"
            name="declaration"
            checked={formData.declaration}
            onChange={handleInputChange}
            required
          />
          <span>I declare that all the information provided is true and accurate *</span>
        </label>
      </div>
    </>
  );

  return (
    <div className="form-page-container">
      <div className="form-page-header">
        <a href="#" onClick={() => navigate(-1)} className="form-page-back-link">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Draft Selection
        </a>
        <h1 className="form-page-title">Create Property Document</h1>
        <p className="form-page-subtitle">Fill in the details below to generate a professional property document</p>
      </div>

      <div className="form-page-content">
        <form onSubmit={handleSubmit}>
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}

          {currentStep === totalSteps && (
            <div className="form-page-section">
              <div className="form-page-section-title">📄 Supporting Documents</div>
              <p className="form-page-note">
                Upload sale deeds, property papers, agreements, receipts, or other supporting documents. Extracted text will be added to your property draft.
              </p>
              <DocumentUpload
                onDocumentsChange={handleDocumentsChange}
                maxFiles={8}
                acceptedTypes=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
              />
            </div>
          )}

          <div className="form-page-navigation">
            <div className="form-page-step-indicator">
              Step {currentStep} of {totalSteps}
            </div>
            <div className="form-page-buttons">
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={prevStep}
                  className="form-page-btn form-page-btn-secondary"
                >
                  Previous
                </button>
              )}
              {currentStep < totalSteps ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="form-page-btn form-page-btn-primary"
                >
                  Next
                </button>
              ) : (
                <button
                  type="submit"
                  className="form-page-btn form-page-btn-primary"
                >
                  Generate Property Document
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PropertyFormPage;
